import React from "react";
import { AppIcon } from "components/AppIcon";
import { set } from "lodash";
/**
 * OrderChargesView
 * -----------------
 * View-only widget that renders the selected shipping option(s) and special fee(s)
 * using the exact shapes you produce in create-order payloads:
 *
 *   shipping_option: [
 *     {
 *       shipping__id: string,            // code or id
 *       shipping_name: string,
 *       shipping_amount: number,
 *       shipping_tax_percentage: number,
 *       shipping_tax_amount: number,
 *       shipping_taxable: boolean,
 *       shipping_delivarydate?: string,
 *       shipping_delivery_time?: string,
 *     }
 *   ]
 *
 *   specialfee: [
 *     {
 *       specialfee_id: string,
 *       specialfee_name: string,
 *       specialfee_amount: number,
 *       specialfee_tax_percentage: number,
 *       specialfee_tax_amount: number,
 *       specialfee_taxable: boolean,
 *     }
 *   ]
 */

import { getAuthCookie } from "../../utils/authCookies";
import { showError } from "toast/toast";

export type ShippingRow = {
  shipping__id: string;
  shipping_name: string;
  shipping_amount: number;
  shipping_tax_percentage: number;
  shipping_tax_amount: number;
  shipping_taxable: boolean;
  shipping_delivarydate?: string;
  shipping_delivery_time?: string;
};

export type SpecialFeeRow = {
  specialfee_id: string;
  specialfee_name: string;
  specialfee_amount: number;
  specialfee_tax_percentage: number;
  specialfee_tax_amount: number;
  specialfee_taxable: boolean;
  required?: boolean;
};

export type OrderChargesViewProps = {
  shipping_option?: ShippingRow[] | null;
  specialfee?: SpecialFeeRow[] | null;
  className?: string;
};


const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;
const pct = (n: any) => `${Number(n || 0).toFixed(2)}%`;

export const OrderChargesView: React.FC<OrderChargesViewProps> = ({
  shipping_option,
  specialfee,
  className,
}) => {
  const shipping = Array.isArray(shipping_option) ? shipping_option : [];
  const fees = Array.isArray(specialfee) ? specialfee : [];

  return (
    <div className={className}>
      {/* Shipping block */}
      <div className="card rounded-4 mb-3">
        <div className="border-0 py-2 px-4 d-flex align-items-center rounded-top" style={{ backgroundColor: "#eff6ff" }}>
          <AppIcon name="shippingOptions" size={22} alt="Shipping Options" className="me-2" />
          <h6 className="mb-0">Shipping Options</h6>
        </div>
        <div className="card-body">
          {shipping.length === 0 && <div className="text-muted">No shipping selected</div>}
          {shipping.map((s) => (
            <div key={s.shipping__id} className="d-flex justify-content-between align-items-start p-3 rounded-3 border mb-2">
              <div>
                <div className="fw-semibold">{s.shipping_name || "Shipping"}</div>
                <div className="text-secondary small">
                  {(s.shipping_delivarydate || s.shipping_delivery_time) ? (
                    <>
                      {s.shipping_delivarydate && <span>{s.shipping_delivarydate}</span>}
                      {s.shipping_delivarydate && s.shipping_delivery_time && <span> • </span>}
                      {s.shipping_delivery_time && <span>{s.shipping_delivery_time}</span>}
                    </>
                  ) : (
                    <span>Selected service</span>
                  )}
                </div>
                {/* {(s.shipping_tax_amount || s.shipping_tax_percentage) ? (
                  <div className="small mt-1">
                    Tax: <strong>{money(s.shipping_tax_amount)}</strong>{" "}
                    <span className="text-secondary">({pct(s.shipping_tax_percentage)})</span>
                  </div>
                ) : null} */}
              </div>
              <div className="fs-5 fw-semibold">{money(s.shipping_amount)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Special Fee block */}
      <div className="card rounded-4">
        <div className="border-0 py-3 px-4 d-flex align-items-center rounded-top" style={{ backgroundColor: "#eff6ff" }}>
          <AppIcon name="dollar3" size={22} alt="Special Fee" className="me-2" />
          <h6 className="mb-0">Special Fee</h6>
        </div>
        <div className="card-body">
          {fees.length === 0 && <div className="text-muted">No special fee selected</div>}
          {fees.map((f) => (
            <div key={f.specialfee_id} className="d-flex justify-content-between align-items-center p-3 rounded-3 border mb-2">
              <div>
                <div className="fw-semibold">
                  {f.specialfee_name}
                  {f.required === true && <span className="ms-1 text-danger" title="Required">*</span>}
                </div>
                {/* {(f.specialfee_tax_amount || f.specialfee_tax_percentage) ? (
                  <div className="small mt-1">
                    Tax: <strong>{money(f.specialfee_tax_amount)}</strong>{" "}
                    <span className="text-secondary">({pct(f.specialfee_tax_percentage)})</span>
                  </div>
                ) : null} */}
              </div>
              <div className="fs-5 fw-semibold">{money(f.specialfee_amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


/**
 * TaxCloudButtons
 * ----------------
 * A standalone widget that controls the two-button flow on the view page.
 * It DOES NOT fetch manual taxes (manual taxes should already be baked into the
 * rows you pass to OrderChargesView). For TaxCloud, it will:
 *   1) fetch active type → if taxcloud, show "Get Live Taxes"; else nothing
 *   2) on click: upsert cart with products + optional shipping line + taxable fee lines
 *   3) on success: call onApplyTaxes with the computed totals so parent can update payload
 *   4) show "Report Live Taxes"; clicking it will commit + complete and then hide
 */

// === Imports needed from your global helper file (adjust the import path) ===
// import { toTaxCloudAddressFromObject, taxcloudUpsertForSales_v2, taxcloudCreateOrder_v2, completeTaxCloudOrderV2 } from "../specialFeeTaxCloud";

// To keep this file drop-in, we accept those functions via props too.
export type TaxCloudHelpers = {
  toAddress(obj: any): { addressLine1: string; city: string; state: string; zip: string } | null;
  upsertSales(args: {
    token: string;
    supplierId: string;
    destination: { addressLine1: string; city: string; state: string; zip: string };
    products: { sku: string; price: number; quantity: number }[];
    shipping?: { id?: string; cost?: number } | null;
    fees?: { id?: string; amount: number; taxable?: boolean }[];
    orderNumber?: string;
  }): Promise<any>;
  createOrder(token: string, supplierId: string, cartId: string, orderNumber?: string): Promise<any>;
  completeOrder(token: string, supplierId: string, orderId: string, completedDateISO?: string): Promise<any>;
};

export type TaxCloudButtonsProps = {
  token: string;
  supplierId: string;         // goes in URL paths
  tenantIdForActiveType: string; // e.g. 68a45fdb3cf0fa2d36b2d673
  cart_id?: string;          // optional cart id to use instead of order number
  order: any;                 // full order (for address + order_number + payment_status)
  items: { sku: string; price: number; quantity: number }[]; // simple product lines
  shipping_option?: ShippingRow[] | null;      // first entry is considered the selected shipping
  specialfee?: SpecialFeeRow[] | null;         // may contain multiple (we'll include taxable ones)
  onApplyTaxes?: (data: {
    tax_amount: number;                // overall tax
    product_tax?: number;
    shipping_tax?: number;
    specialfee_tax?: number;
    lineByItemId?: Record<string, { amount: number; rate: number }>; // per line if available
  }) => void;
  address?: any;
  onAfterGetLiveTaxes?: () => void | Promise<void>;
  onAfterReportSalesTax?: () => void | Promise<void>;
  helpers: TaxCloudHelpers;
};

export type TaxCloudButtonsHandle = {
  reportLiveTaxes: () => Promise<boolean>;
};


export const TaxCloudButtons = React.forwardRef<TaxCloudButtonsHandle, TaxCloudButtonsProps>(function TaxCloudButtons({
  token,
  supplierId,
  tenantIdForActiveType,
  order,
  address,
  items,
  shipping_option,
  specialfee,
  onApplyTaxes,
  onAfterGetLiveTaxes,     // ✅ add
  onAfterReportSalesTax,  // ✅ add
  helpers,
  cart_id,
}, ref) {
  const [taxType, setTaxType] = React.useState<"manual" | "taxcloud" | "geera" | "">("");
  const [fetched, setFetched] = React.useState(false);
  const [committed, setCommitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [reportloading, setReportLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [previewTax, setPreviewTax] = React.useState<number | null>(null);
  const [cartId, setCartId] = React.useState<string>("");
  const reportAttemptedRef = React.useRef(false);
  // 1) get active type

  // console.log("tenantIdForActiveType",   shipping_option);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/tax/active-type/${tenantIdForActiveType}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        const t = String(data?.taxType || data?.type || "").toLowerCase();
        if (t === "manual" || t === "taxcloud" || t === "geera") setTaxType(t as any);
        else setTaxType("");
      } catch (e) {
        setTaxType("");
      }
    })();
  }, [token, tenantIdForActiveType]);

  const getSelectedShipping = () => {
    const row = (shipping_option || [])[0];
    if (!row) return null;
    return { id: row.shipping_name || "SHIP001", cost: Number(row.shipping_amount || 0) };
  };

  const getTaxableFees = () =>
    (specialfee || [])
      .filter((f) => f && f.specialfee_taxable !== false && Number(f.specialfee_amount) > 0)
      .map((f) => ({ id: f.specialfee_name, amount: Number(f.specialfee_amount), taxable: true }));

  // ✅ Extract core fetch logic that returns cartId
  const fetchLiveTaxesCore = async (): Promise<string | null> => {
    if (!destination) {
      throw new Error("Missing shipping address on order");
    }

    const shipping = getSelectedShipping();
    const fees = getTaxableFees();
    const productsForTax = Array.isArray(items) ? items : [];
    const hasAnyCartLine =
      productsForTax.some((p: any) => Number(p?.quantity || 0) > 0 && Number(p?.price || 0) >= 0) ||
      Number(shipping?.cost || 0) > 0 ||
      (Array.isArray(fees) && fees.some((f: any) => Number(f?.amount || 0) > 0));

    // if (!hasAnyCartLine) {
    //   throw new Error("Selected product is not taxable: Review product configuration");
    // }

    const resp = await helpers.upsertSales({
      token,
      supplierId,
      destination,
      products: productsForTax,
      shipping,
      fees,
      orderNumber: cart_id,
    });

    // Parse totals & per-line
    const carts = Array.isArray(resp?.items) ? resp.items : [];
    const lines = carts.flatMap((c: any) => (Array.isArray(c?.lineItems) ? c.lineItems : []));
    const byId: Record<string, { amount: number; rate: number }> = {};
    let product = 0, shippingTax = 0, feeTax = 0;

    const shipId = shipping?.id || "SHIP001";
    const feeIds = new Set((fees || []).map((f) => f.id));

    for (const ln of lines) {
      const id = String(ln?.itemId || "");
      const amt = Number(ln?.tax?.amount || 0);
      const rate = Number(ln?.tax?.rate || 0);
      if (!id) continue;
      if (id === shipId) shippingTax += amt;
      else if (feeIds.has(id)) feeTax += amt;
      else { product += amt; byId[id] = { amount: amt, rate }; }
    }

    const total = Number((product + shippingTax + feeTax).toFixed(2));
    const newCartId = String(resp?.cartId || resp?.items?.[0]?.cartId || resp?.data?.cart?.id || "");
      
    // Update state
    setPreviewTax(total);
    setFetched(true);
    setCartId(newCartId);

    onApplyTaxes?.({ tax_amount: total, product_tax: product, shipping_tax: shippingTax, specialfee_tax: feeTax, lineByItemId: byId });

    if (onAfterGetLiveTaxes) {
      await onAfterGetLiveTaxes();
    }

    return newCartId;
  };

  const handleFetchLiveTax = async () => {
    setLoading(true);
    setError(null);
    try {
      const cartIdResult = await fetchLiveTaxesCore();
      if (!cartIdResult) {
        throw new Error("Failed to fetch live taxes - no cart ID returned");
      }
    } catch (e: any) {
      const msg = e?.message || "Failed to fetch live taxes";
      setError(msg);
      setFetched(false);
    } finally {
      setLoading(false);
    }
  };
  const handleReportSalesTax = React.useCallback(async (): Promise<boolean> => {
    reportAttemptedRef.current = true;
    setReportLoading(true);
    setError(null);
    try {
      // ✅ First, fetch live taxes if not already fetched
      let currentCartId = cartId;
      if (!fetched || !currentCartId) {
        try {
          currentCartId = await fetchLiveTaxesCore() || "";
          if (!currentCartId) {
            const msg = "Failed to fetch live taxes - no cart ID returned";
            setError(msg);
            return false;
          }
        } catch (fetchError: any) {
          const msg = fetchError?.message || "Failed to fetch live taxes";
          setError(msg);
          return false;
        }
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud_order/version?order_type=${order?.order_type}&order_number=${order?.order_number}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      const versionData = await res.json();

      const full_number = versionData?.full_number || order?.order_number || "";
      const orderNumber = String(full_number || "");
      const tcCartId = currentCartId || ""; // use the fetched cartId

      // ✅ Always run both createOrder and completeOrder, regardless of createOrder's result
      let createOrderError: any = null;
      let completeOrderError: any = null;

      // Run createOrder (catch error but continue)
      try {
        await helpers.createOrder(token, supplierId, tcCartId, orderNumber);
      } catch (e: any) {
        createOrderError = e;
        console.error("[TAX_REPORT] createOrder failed:", e);
      }

      // Always run completeOrder regardless of createOrder result
      try {
        await helpers.completeOrder(token, supplierId, orderNumber, new Date().toISOString());
      } catch (e: any) {
        completeOrderError = e;
        console.error("[TAX_REPORT] completeOrder failed:", e);
      }

      // If both failed, show error; otherwise proceed
      if (createOrderError && completeOrderError) {
        throw new Error(`Both operations failed: createOrder - ${createOrderError?.message || "Unknown error"}, completeOrder - ${completeOrderError?.message || "Unknown error"}`);
      } else if (createOrderError) {
        // createOrder failed but completeOrder succeeded - log warning but continue
        console.warn("[TAX_REPORT] createOrder failed but completeOrder succeeded:", createOrderError);
      } else if (completeOrderError) {
        // completeOrder failed - this is more critical
        throw completeOrderError;
      }

      // ✅ lock the UI so the report buttons disappear
      setCommitted(true);
      // (optional) this clears the preview flag so "Get live tax" flow doesn't re-appear
      setFetched(false);

      // console.log("[TAX_REPORT] Successfully reported sales tax for order:", orderNumber);

      // ✅ let parent persist again after reporting success
      if (onAfterReportSalesTax) {
        await onAfterReportSalesTax();
      }
      return true;
    } catch (e: any) {
      console.error("[TAX_REPORT] Failed to report live taxes:", e);
      const msg = e?.message || "Failed to report live taxes";
      setError(msg);
      return false;
    } finally {
      setReportLoading(false);
    }
  }, [token, supplierId, cartId, order?.order_number, order?.id, order?.order_type, helpers, onAfterReportSalesTax, fetched, fetchLiveTaxesCore]);

  React.useImperativeHandle(ref, () => ({
    reportLiveTaxes: handleReportSalesTax,
  }), [handleReportSalesTax]);

  // ✅ Auto-trigger tax reporting for non-SIGNAGE_FIRM users when payment is completed
  React.useEffect(() => {
    const licenseType = getAuthCookie("licenseType");
    const paymentStatus = String(order?.payment_status || "").toLowerCase();

    // console.log("[AUTO_TAX_REPORT] Checking conditions:", {
    //   licenseType,
    //   paymentStatus,
    //   taxType,
    //   committed,
    //   loading,
    //   orderId: order?.id,
    //   orderNumber: order?.order_number
    // });

    // Only auto-report if:
    // 1. License is NOT SIGNAGE_FIRM
    // 2. Payment status is "Paid"
    // 3. Tax type is TaxCloud
    // 4. Not already committed
    // 5. Not currently loading
    // 6. Order ID exists
    // 7. Not already attempted (prevents loop when API returns 500)
    if (
      licenseType !== "SIGNAGE_FIRM" &&  licenseType !== "SIGNAGE_TEAM_MEMBER" &&
      paymentStatus === "paid" &&
      taxType === "taxcloud" &&
      !committed &&
      !loading &&
      order?.id &&
      !reportAttemptedRef.current
    ) {
      // console.log("[AUTO_TAX_REPORT] ✅ Auto-triggering tax report for order:", order?.order_number);
      handleReportSalesTax();
    }
  }, [order?.id, order?.payment_status, order?.order_number, taxType, committed, loading, handleReportSalesTax]);

  // Prefer the explicit `address` prop; else fall back to order variants
  // put near other hooks
  // 1️⃣ Memoize computed address only
  const destination = React.useMemo(() => {
    const a = helpers.toAddress(order?.shipping_address?.address_details);
    return a ?? null;
  }, [order?.shipping_address?.address_details, helpers]);

  // // 2️⃣ Handle loading state with useEffect
  // React.useEffect(() => {
  //   if (!order?.shipping_address?.address_details) return;

  //   setLoading(true);

  //   const a = helpers.toAddress(order?.shipping_address?.address_details);

  //   if (a) {
  //     setLoading(false);
  //   } else {
  //     setLoading(false);
  //   }
  // }, [order?.shipping_address?.address_details, helpers]);


  // console.log("destination123", destination);
  // when destination appears, clear blocks
  // Build a stable key from normalized address so we only reset when address truly changes
  const destResetKey = React.useMemo(() => {
    const a = destination;
    return a ? `${a.addressLine1 || ""}|${a.city || ""}|${a.state || ""}|${a.zip || ""}` : "";
  }, [destination?.addressLine1, destination?.city, destination?.state, destination?.zip]);

  const prevDestResetKeyRef = React.useRef(destResetKey);
  React.useEffect(() => {
    // Only clear when the normalized address actually changed
    if (destResetKey && destResetKey !== prevDestResetKeyRef.current) {
      setFetched(false);
      setError(null);
    }
    prevDestResetKeyRef.current = destResetKey;
  }, [destResetKey]);


  // When a valid destination appears, clear prior "fetched" block & errors

  const canFetch = !!destination;
  const shouldHide = committed || taxType !== "taxcloud";
  // Toast whenever inline `error` is set (same message as under the buttons).
  React.useEffect(() => {
    if (error) showError(error);
  }, [error]);

  return (
    <div className="d-flex gap-3 mt-3 mb-3 p-0">
      {shouldHide ? null : (
        <div className="d-flex flex-column gap-2 mt-3 mb-3 p-0">
          <div className="d-flex gap-3 flex-wrap align-items-center">
            <button
              type="button"
              className="btn export-button"
              disabled={!canFetch || loading}
              onClick={handleFetchLiveTax}
            >
              {loading ? "Fetching…" : "Get Live Taxes"}
            </button>

            {/* {order?.order_tax_summary?.tax_status == "draft" && (
              <button
                type="button"
                className="btn btn-primary table-addbutton"
                disabled={reportloading}
                onClick={handleReportSalesTax}
              >
                {reportloading ? "Reporting…" : "Report Live Taxes"}
              </button>
            )} */}
          </div>

          {/* {error && (
            <div className="small text-danger fs-14" role="alert">
              {error}
            </div>
          )} */}
        </div>
      )}
    </div>
  );
});

export default OrderChargesView;
